/**
 * Workspace-scoped artifact kind palette + filter choices.
 *
 * Lifted from
 * `pages/ProjectDetail/sections/Library/library.constants.ts` so multiple
 * surfaces (Library, brief-generation intake, …) can consume the same
 * visual language without crossing into a page slice.
 *
 * The original `library.constants.ts` re-exports these symbols from this
 * module for back-compat; existing consumers keep working unchanged.
 */
import { createElement } from 'react';
import type { ArtifactKind } from '@inference.engine/widgets';
import {
  BoxIcon,
  FileIcon,
  FileUser,
  FlaskConicalIcon,
  ImageIcon,
  LayoutDashboardIcon,
  LightbulbIcon,
  MailIcon,
  MessageSquareIcon,
  ScanSearchIcon,
  Siren,
  SlidersIcon,
  SparklesIcon,
  Type,
  UsersIcon,
  VideoIcon,
  Waypoints,
  type LucideIcon,
} from 'lucide-react';
import type { BadgeProps } from '@monksflow/monks-ui';
import { i18n } from '@/shared/i18n';
import type { FilterChipOption } from '@/shared/ui';

export type ArtifactKindPaletteEntry = {
  label: string;
  /** The kind's chip colour, always a Tessra Badge variant. */
  variant: NonNullable<BadgeProps['variant']>;
  /** Surface and foreground for non-badge tiles, in the variant's theme tokens. */
  bg: string;
  text: string;
  icon: LucideIcon;
};

// Each family pairs a Badge variant with the tokens that variant paints, so a
// placeholder tile reads as the same colour as its chip in both themes. Neutral
// is the exception: `secondary` paints `bg-surface`, the card colour in dark
// mode, so the chip takes `outline` and the tile takes `bg-secondary` instead.
const KIND_TONE = {
  orange: { variant: 'orange', bg: 'bg-badge-orange', text: 'text-badge-orange-foreground' },
  lime: { variant: 'lime', bg: 'bg-badge-lime', text: 'text-badge-lime-foreground' },
  blue: { variant: 'blue', bg: 'bg-badge-blue', text: 'text-badge-blue-foreground' },
  yellow: { variant: 'yellow', bg: 'bg-badge-yellow', text: 'text-badge-yellow-foreground' },
  purple: { variant: 'purple', bg: 'bg-badge-purple', text: 'text-badge-purple-foreground' },
  neutral: { variant: 'outline', bg: 'bg-secondary', text: 'text-secondary-foreground' },
  accent: { variant: 'default', bg: 'bg-accent', text: 'text-accent-foreground' },
} as const satisfies Record<string, Pick<ArtifactKindPaletteEntry, 'variant' | 'bg' | 'text'>>;

// Labels are getters so each read resolves against the active locale; a plain
// `i18n.t(...)` here would freeze the English string at import time.
export const ARTIFACT_KIND_PALETTE = {
  social_opportunities: {
    get label() {
      return i18n.t('common:artifact.artifactKindPalette.opportunity');
    },
    ...KIND_TONE.orange,
    icon: Siren,
  },
  brief: {
    get label() {
      return i18n.t('common:artifact.artifactKindPalette.brief');
    },
    ...KIND_TONE.orange,
    icon: FileUser,
  },
  audience: {
    get label() {
      return i18n.t('common:artifact.artifactKindPalette.persona');
    },
    ...KIND_TONE.orange,
    icon: UsersIcon,
  },
  video: {
    get label() {
      return i18n.t('common:artifact.artifactKindPalette.video');
    },
    ...KIND_TONE.lime,
    icon: VideoIcon,
  },
  email: {
    get label() {
      return i18n.t('common:artifact.artifactKindPalette.email');
    },
    ...KIND_TONE.lime,
    icon: MailIcon,
  },
  image: {
    get label() {
      return i18n.t('common:artifact.artifactKindPalette.image');
    },
    ...KIND_TONE.lime,
    icon: ImageIcon,
  },
  copy: {
    get label() {
      return i18n.t('common:artifact.artifactKindPalette.copy');
    },
    ...KIND_TONE.lime,
    icon: Type,
  },
  ideation: {
    get label() {
      return i18n.t('common:artifact.artifactKindPalette.creativeConcept');
    },
    ...KIND_TONE.lime,
    icon: LightbulbIcon,
  },
  strategy: {
    get label() {
      return i18n.t('common:artifact.artifactKindPalette.strategy');
    },
    ...KIND_TONE.orange,
    icon: Waypoints,
  },
  insight: {
    get label() {
      return i18n.t('common:artifact.artifactKindPalette.insight');
    },
    ...KIND_TONE.neutral,
    icon: SparklesIcon,
  },
  misc: {
    get label() {
      return i18n.t('common:artifact.artifactKindPalette.misc');
    },
    ...KIND_TONE.neutral,
    icon: BoxIcon,
  },
  file: {
    get label() {
      return i18n.t('common:artifact.artifactKindPalette.file');
    },
    ...KIND_TONE.neutral,
    icon: FileIcon,
  },
  custom: {
    get label() {
      return i18n.t('common:artifact.artifactKindPalette.custom');
    },
    ...KIND_TONE.neutral,
    icon: SlidersIcon,
  },
  focus_group: {
    get label() {
      return i18n.t('common:artifact.artifactKindPalette.focusGroup');
    },
    ...KIND_TONE.blue,
    icon: UsersIcon,
  },
  audience_session: {
    get label() {
      return i18n.t('common:artifact.artifactKindPalette.interview');
    },
    ...KIND_TONE.accent,
    icon: MessageSquareIcon,
  },
  content_test: {
    get label() {
      return i18n.t('common:artifact.artifactKindPalette.contentTest');
    },
    ...KIND_TONE.yellow,
    icon: FlaskConicalIcon,
  },
  iq_analysis: {
    get label() {
      return i18n.t('common:artifact.artifactKindPalette.iqAnalysis');
    },
    ...KIND_TONE.purple,
    icon: ScanSearchIcon,
  },
  html_widget: {
    get label() {
      return i18n.t('common:artifact.artifactKindPalette.htmlWidget');
    },
    ...KIND_TONE.blue,
    icon: LayoutDashboardIcon,
  },
} as const satisfies Record<ArtifactKind, ArtifactKindPaletteEntry>;

/** Filter-dropdown icon for a kind, matching the Projects type filter's look. */
function kindFilterIcon(kind: ArtifactKind) {
  return createElement(ARTIFACT_KIND_PALETTE[kind].icon, { className: 'size-4' });
}

// Ordered alphabetically by label so the Type filter reads A→Z.
// `label` delegates through a getter: reading the palette label eagerly here
// would resolve every string at import time and freeze the English copy.
export const ARTIFACT_KIND_FILTER_CHOICES = [
  {
    value: 'brief',
    get label() {
      return ARTIFACT_KIND_PALETTE.brief.label;
    },
    icon: kindFilterIcon('brief'),
  },
  {
    value: 'copy',
    get label() {
      return ARTIFACT_KIND_PALETTE.copy.label;
    },
    icon: kindFilterIcon('copy'),
  },
  {
    value: 'ideation',
    get label() {
      return ARTIFACT_KIND_PALETTE.ideation.label;
    },
    icon: kindFilterIcon('ideation'),
  },
  {
    value: 'custom',
    get label() {
      return ARTIFACT_KIND_PALETTE.custom.label;
    },
    icon: kindFilterIcon('custom'),
  },
  {
    value: 'email',
    get label() {
      return ARTIFACT_KIND_PALETTE.email.label;
    },
    icon: kindFilterIcon('email'),
  },
  {
    value: 'file',
    get label() {
      return ARTIFACT_KIND_PALETTE.file.label;
    },
    icon: kindFilterIcon('file'),
  },
  {
    value: 'focus_group',
    get label() {
      return ARTIFACT_KIND_PALETTE.focus_group.label;
    },
    icon: kindFilterIcon('focus_group'),
  },
  {
    value: 'html_widget',
    get label() {
      return ARTIFACT_KIND_PALETTE.html_widget.label;
    },
    icon: kindFilterIcon('html_widget'),
  },
  {
    value: 'audience_session',
    get label() {
      return ARTIFACT_KIND_PALETTE.audience_session.label;
    },
    icon: kindFilterIcon('audience_session'),
  },
  {
    value: 'image',
    get label() {
      return ARTIFACT_KIND_PALETTE.image.label;
    },
    icon: kindFilterIcon('image'),
  },
  {
    value: 'insight',
    get label() {
      return ARTIFACT_KIND_PALETTE.insight.label;
    },
    icon: kindFilterIcon('insight'),
  },
  {
    value: 'iq_analysis',
    get label() {
      return ARTIFACT_KIND_PALETTE.iq_analysis.label;
    },
    icon: kindFilterIcon('iq_analysis'),
  },
  {
    value: 'misc',
    get label() {
      return ARTIFACT_KIND_PALETTE.misc.label;
    },
    icon: kindFilterIcon('misc'),
  },
  {
    value: 'social_opportunities',
    get label() {
      return ARTIFACT_KIND_PALETTE.social_opportunities.label;
    },
    icon: kindFilterIcon('social_opportunities'),
  },
  {
    value: 'audience',
    get label() {
      return ARTIFACT_KIND_PALETTE.audience.label;
    },
    icon: kindFilterIcon('audience'),
  },
  {
    value: 'strategy',
    get label() {
      return ARTIFACT_KIND_PALETTE.strategy.label;
    },
    icon: kindFilterIcon('strategy'),
  },
  {
    value: 'video',
    get label() {
      return ARTIFACT_KIND_PALETTE.video.label;
    },
    icon: kindFilterIcon('video'),
  },
  {
    value: 'content_test',
    get label() {
      return ARTIFACT_KIND_PALETTE.content_test.label;
    },
    icon: kindFilterIcon('content_test'),
  },
] as const satisfies ReadonlyArray<FilterChipOption<ArtifactKind>>;
